import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthContext } from "../../providers";
import { useRoleBasedNavigation } from "../../hooks";
import { config } from "../../config/env";
import { authInterceptor } from "../../services/Authentication/authInterceptor";
import { secureStorage } from "../../services/Storage/secureStorage";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
  allowedRoles?: string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  redirectTo = "/login",
  allowedRoles = [],
}) => {
  // Temporarily disabled: All routes are now unprotected
  // This allows free access to all pages for development/testing
  return <>{children}</>;
  
  /* Original protection logic - commented out for now
  const { isAuthenticated, loading, user } = useAuthContext();
  const location = useLocation();
  const { getDashboardUrl } = useRoleBasedNavigation();

  // Allow access immediately if dev bypass or mock credentials mode is enabled
  if (config.dev.bypassAuth || config.dev.mockCredentials) {
    // In mock credentials mode, allow access - tokens will be restored on the page
    // This prevents redirect loops when navigating after onboarding
    if (config.dev.mockCredentials) {
      // Always allow access in mock mode - the page will restore tokens if needed
      return <>{children}</>;
    } else {
      // Full bypass mode - always allow
      return <>{children}</>;
    }
  }

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          color: "white",
          fontSize: "1.2rem",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>🔐</div>
          <div>Verifying authentication...</div>
        </div>
      </div>
    );
  }

  // Redirect unauthenticated users from protected routes
  if (requireAuth && !isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Redirect authenticated users from public routes (login, register) to onboarding
  if (!requireAuth && isAuthenticated) {
    return <Navigate to="/onboarding" replace />;
  }

  // Check role-based access if roles are specified
  if (requireAuth && allowedRoles.length > 0) {
    const userRole = user?.user_metadata?.role || "user";
    if (!allowedRoles.includes(userRole)) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            color: "white",
            fontSize: "1.2rem",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>🚫</div>
            <div>Access Denied</div>
            <div
              style={{ fontSize: "0.9rem", marginTop: "0.5rem", opacity: 0.8 }}
            >
              You don't have permission to access this page
            </div>
            <div
              style={{ fontSize: "0.8rem", marginTop: "1rem", opacity: 0.6 }}
            >
              Required role: {allowedRoles.join(" or ")}
            </div>
            <div
              style={{ fontSize: "0.8rem", marginTop: "0.5rem", opacity: 0.6 }}
            >
              Your role: {userRole}
            </div>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
  */
};
